import React from 'react';
import { createStore } from 'redux';
import { detect } from 'detect-browser';
import * as logger from '@happyreturns/jscommons';

const initialState = {
  // fetch method we should use throughout the codebase
  // TODO: figure out what we wanna do for this codebase and set it up
  fetch: undefined,
};

const store = createStore(
  // reducer
  (state = initialState, action) => ({ ...state, ...action.value }),
);

// returns true if cloning responses is supported on this browser
function canCloneResponses() {
  // It appears that Safari versions <= 11 cannot clone responses, we see an inability
  // to do so by one of our users using Mobile Safari 11.0 ... this thread says that
  // only 11.1 and above can clone -- https://github.com/github/fetch/issues/504

  const browser = detect();
  if (browser === null) {
    return false;
  }

  // version is typically something lke "12.1.2", split so we can access major version
  const versionArray = browser.version.split('.');

  // only allow cloning for safari 12 and above, just to be safe
  if (
    browser.name.includes('safari') &&
    versionArray.length > 0 &&
    versionArray[0] <= 11
  ) {
    return false;
  }

  return true;
}

// returns hex strings e.g. "#FF0000" for some site-wide colors
// the switch statement within documents the allowable input values for "name"
// (currently i see no need to make consts out of the color names, but if we expand this system
//  we should consider it -- ss)
function getColor(name) {
  switch (name) {
    case 'primary':
      return STYLES && STYLES.primary_color ? STYLES.primary_color : '#000000';
    case 'gray-light':
      return '#E7E7E7';
    case 'grayish':
      return '#B4B0AC';
    case 'gray':
      return '#A2A2A2';
    case 'gray-dark':
      return '#737373';
    default:
      return '#000000';
  }
}

// returns the formatted url to load the provided tracking # on the provided carrier's page
// returns "undefined" if carrier is unknown or for some other reason the URL can't be made
function getCarrierTrackingPageURL(carrier, tracking, trackingInfo) {
  if (trackingInfo != null) {
    if (trackingInfo.tracking_url !== '') {
      return trackingInfo.tracking_url;
    }

    if (trackingInfo.tracking !== '') {
      tracking = trackingInfo.tracking;
    }
  }

  switch (carrier) {
    case 'fedex':
      return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${tracking}`;
    case 'ups':
    case 'ups-easypost':
      return `https://www.ups.com/track?tracknum=${tracking}`;
    case 'usps':
      return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${tracking}`;
    case 'echo':
      return `https://www.echo.com/shipment-tracking?ref=${tracking}`;
    case 'mothership':
      return `https://dashboard.mothership.com/track/${tracking}`;
    default:
      logger.Error('Cannot get URL for unknown carrier ', carrier);
      return undefined;
  }
}

// returns a human readable version of the provided carrier
// where carrier is the carrier ID that is used in the shipping service
//
// e.g. for fedex will return "FedEx"
// if carrier isunrecognized will return the empty string
function humanReadableName(carrier) {
  switch (carrier) {
    case 'fedex':
      return 'FedEx';
    case 'ups-easypost':
    case 'ups':
      return 'UPS';
    case 'usps':
      return 'USPS';
    case 'dhl':
      return 'DHL';
    case 'dhlecommerce':
      return 'DHL Ecommerce';
    case 'royalmail':
      return 'Royal Mail';
    case 'purolator':
      return 'Purolator';
    case 'pitneybowes':
      return 'Pitney Bowes';
    case 'canadapost':
      return 'Canada Post';
    case 'australiapost':
      return 'Australia Post';
    case 'mothership':
      return 'Mothership';
    case 'echo':
      return 'ECHO';
  }
  return undefined;
}

// This function performs a basic fetch, with logging
const loggedFetch = async function (url, args) {
  // Only log errors or unexpected status codes
  try {
    const response = await fetch(url, args);
    // Only log non-2XX responses
    if (!response.ok) {
      let resp = '';
      if (canCloneResponses()) {
        const clone = response.clone();
        resp = await clone.text();
      }
      logger.Warning(`Fetch error: ${url} returned ${response.status}`, {
        url: url,
        status: response.status,
        response: resp,
      });
    }
    return response;
  } catch (err) {
    logger.Error(`Fetch failed for ${url}: ${err.message}`, {
      url: url,
      error: err.message,
    });
    throw err;
  }
}

export {
  getColor,
  getCarrierTrackingPageURL,
  humanReadableName,
  store,
  loggedFetch,
};
